/* eslint-disable react/display-name */
import React, { useEffect, useRef } from 'react';
import AddIcon from '@material-ui/icons/Add';
import Header from '../../../components/Header';
import { Flex } from '../../../components/NavBar/styles';
import AddBtn from '../../../components/Botoes/AddBtn';
import { Body } from '../../../components/Body';
import { config } from '../../../components/Tabela';
import useStore from '../../../services/hooks/useStore';
import MaterialTable, { Column } from 'material-table';
import { useLocalStore, observer } from 'mobx-react-lite';
import useModal from '../../../services/hooks/useModal';
import ActionButton from '../../../components/Tabela/ActionButton';
import Confirmar from '../../../components/Modais/Confirmar';
import FormCategoriaNFe from './FormCategoriaNFe';
import { useHistory } from 'react-router-dom';
import { apiV2 } from '../../../services/utils';
import { permissoes } from '../../../services/utils/permissoes';
import X9Logger from '../../../services/X9Logger';

type State = {
  selectedCategoriaNFe: string;
  sync: boolean;
  pageSize: number;
  setPageSize(value: number): void;
  page: number;
  setPage(num: number): void;
};

const CategoriaNFe: React.FC = () => {
  const tableRef = useRef();
  const store = useStore();
  const history = useHistory();
  const modalCategoriaNFe = useModal();
  const modalExclusao = useModal();

  const state = useLocalStore(
    (): State => ({
      selectedCategoriaNFe: '',
      sync: false,
      pageSize: 0,
      setPageSize(value: number) {
        this.pageSize = value + 1;
      },
      setPage(num: number) {
        this.page = num;
      },
      page: 0,
    }),
  );

  store.propriedade && store.validarPermissao(permissoes.NOTA_FISCAL_CATEGORIA_NFE_LEITURA, history);
  const temPermissaoEscrita = store.checkPermissions(permissoes.NOTA_FISCAL_CATEGORIA_NFE_ESCRITA);

  const columns: Column<CategoriaNFe>[] = [
    {
      field: 'acoes',
      disableClick: true,
      render: (row: any) => (
        <ActionButton
          editar={
            !temPermissaoEscrita
              ? undefined
              : () => {
                  state.selectedCategoriaNFe = row.uuid;
                  modalCategoriaNFe.open();
                }
          }
          excluir={
            !temPermissaoEscrita
              ? undefined
              : () => {
                  state.selectedCategoriaNFe = row.uuid;
                  modalExclusao.open();
                }
          }
        />
      ),
      ...(true && ({ width: 35 } as object)),
    },
    {
      field: 'categoria_financeira_nome',
      title: 'Categoria',
      render: ({ categoria_financeira_nome }: any) => {
        return categoria_financeira_nome || '-';
      },
      ...(true && ({ width: 200 } as object)),
    },
    {
      field: 'ncm',
      title: 'Código NCM',
      ...(true && ({ width: 120 } as object)),
    },
    {
      field: 'unidade_medida',
      title: 'Unidade Medida',
      render: ({ unidade_medida }: any) => {
        return unidade_medida?.toUpperCase() || '-';
      },
      ...(true && ({ width: 120 } as object)),
    },
    {
      field: 'descricao_fiscal',
      title: 'Descrição do Produto',
      ...(true && ({ width: 250 } as object)),
    },
    {
      field: 'codigo_produto',
      title: 'Código Produto',
      ...(true && ({ width: 120 } as object)),
    },
    {
      field: 'icms_nota',
      title: 'ICMS na NF',
      render: ({ icms_nota }: any) => {
        return icms_nota ? 'Sim' : 'Não';
      },
      ...(true && ({ width: 100 } as object)),
    },
    {
      field: 'pis',
      title: 'PIS',
      render: ({ pis }: any) => {
        return pis || '-';
      },
      ...(true && ({ width: 100 } as object)),
    },
    {
      field: 'cofins',
      title: 'COFINS',
      render: ({ cofins }: any) => {
        return cofins || '-';
      },
      ...(true && ({ width: 120 } as object)),
    },
    {
      field: 'cfop',
      title: 'CFOP',
      render: ({ n_cfop, cfop }: any) => {
        return n_cfop || cfop || '-';
      },
      ...(true && ({ width: 100 } as object)),
    },
    {
      field: 'ativo',
      title: 'Ativo',
      render: ({ ativo }: any) => {
        return ativo ? 'Sim' : 'Não';
      },
      ...(true && ({ width: 100 } as object)),
    },
  ];

  async function sync(): Promise<void> {
    if (store.propriedade) {
      await store.categoriasNFe.populate();
      state.sync = true;
    }
  }

  useEffect(() => store.setTitulo('Categoria NFe'), []);

  useEffect(() => {
    if (store.propriedade) {
      sync();
      (tableRef.current as any)?.onQueryChange();
    }
    // eslint-disable-next-line
  }, [store.propriedade]);

  async function deletar(): Promise<void> {
    modalExclusao.close();
    store.toggleLoader();
    try {
      await store.categoriasNFe.excluir(state.selectedCategoriaNFe);
      (tableRef.current as any)?.onQueryChange();
    } catch (err: any) {
      X9Logger.report(err as Error);
      if (err.response && err.response.data) {
        Object.values(err.response.data).forEach((value) => {
          store.notificar(`${value}`);
        });
      }
    } finally {
      store.toggleLoader();
    }
  }

  const ExcluirCategoriaNFe: React.FC = () => {
    const categoriaNFe = store.categoriasNFe.getByUuid(state.selectedCategoriaNFe);

    return (
      <div style={{ width: '100%' }}>
        <b>Categoria: </b> {categoriaNFe?.categoria_financeira_nome || '-'}
        <br />
        <b>Descrição do Produto: </b> {categoriaNFe?.descricao_fiscal || '-'}
      </div>
    );
  };

  return state.sync ? (
    <>
      <Header>
        <Flex>
          <AddBtn
            disabled={!temPermissaoEscrita}
            onClick={() => {
              state.selectedCategoriaNFe = '';
              modalCategoriaNFe.open();
            }}
            text="ADICIONAR CATEGORIA"
            Icon={AddIcon}
          />
        </Flex>
      </Header>

      <Body grid={true}>
        <div style={{ overflowX: 'hidden', borderRadius: '10px' }}>
          <MaterialTable
            tableRef={tableRef}
            columns={columns}
            data={(query) =>
              new Promise((resolve) => {
                try {
                  store.propriedade &&
                    apiV2
                      .get('/categoria-nfe/', {
                        params: {
                          page_size: 50,
                          page: query.page + 1,
                          prop: store.propriedade!.id,
                        },
                        headers: {
                          Authorization: `${store.token}`,
                        },
                      })
                      .then((res) => {
                        if (res.data.count) {
                          if (res.data.count >= 50) {
                            state.setPageSize(50);
                          } else {
                            state.setPageSize(res.data.count);
                          }
                        } else {
                          state.setPageSize(0);
                        }

                        resolve({
                          data: res.data || [],
                          page: query.page,
                          totalCount: res.data.count || 0,
                        });
                      });
                } catch (err) {
                  X9Logger.report(err as Error);
                  store.notificar('Ocorreu um erro ao adquirir informações. Tente novamente mais tarde!');
                  resolve({
                    data: [],
                    page: query.page,
                    totalCount: 0,
                  });
                }
              })
            }
            options={{ ...config.options, pageSize: 50, maxBodyHeight: window.innerHeight - 275, sorting: false }}
            localization={config.localization}
            style={config.style}
          />
        </div>
      </Body>
      {modalCategoriaNFe.state ? (
        <FormCategoriaNFe
          open={modalCategoriaNFe.state}
          handleClose={() => {
            modalCategoriaNFe.close();
            state.selectedCategoriaNFe = '';
            (tableRef.current as any)?.onQueryChange();
          }}
          categoriaNFeUuid={state.selectedCategoriaNFe}
          onSuccess={() => {
            (tableRef.current as any)?.onQueryChange();
          }}
        />
      ) : null}
      <Confirmar
        open={modalExclusao.state}
        content={<ExcluirCategoriaNFe />}
        title="Tem certeza que deseja excluir?"
        handleClose={modalExclusao.close}
        onCancel={modalExclusao.close}
        onConfirm={deletar}
      />
    </>
  ) : null;
};

export default observer(CategoriaNFe);

